import React from 'react';

import { Divider, Flex, Grid, GridItem } from '@chakra-ui/react';
import styled from '@emotion/styled';
import { checkUserIsReviewAdmin } from 'utils/helpers/rolesAccess';

import { ReviewsIcon } from 'assets/assetsV1/iconComponents';
import NoDataImg from 'assets/assetsV1/illustrations/newIllustrations/noReviewV1.svg';

import { getModuleSettingsRoute } from 'constants/helper';
import { sendNavigatedToReviewsEvent } from 'constants/mixpanelHelpers/reviewsEvents';
import { routes } from 'constants/routes';

import { Avatar } from 'uiToolkitV1/Avatar';
import { DateComp } from 'uiToolkitV1/DateComp';
import { SkeletonLoader } from 'uiToolkitV1/SkeletonLoader';
import { TextBodyMedium, TextLabel } from 'uiToolkitV1/Typography';

import { EmptySummaryCard } from '../../EmptyCard/EmptySummaryCard';
import { MySummaryCardTemplate } from '../MySummaryCardTemplate';
import { ReviewActionButton } from './ReviewActionButton';
import { useMySummaryReviews } from './hooks';

const PROVIDER_TYPE_TITLE_MAP = {
  SELF: 'Self',
  MANAGER: 'Manager',
  PEER: 'Peer',
  UPWARD: 'Upward',
  NOMINATOR: 'Peer Nomination',
};

const Container = styled(Flex)`
  padding: 12px 8px;
  &:hover {
    background: ${(props) => props.theme.colors.neutral.F6FAFE};
    & .highlightTextOnHover {
      color: #1565bc;
    }
  }
`;

const ButtonContainer = styled(Flex)`
  height: 100%;
  visibility: hidden;
  align-items: center;
  & .action {
    margin-left: auto;
  }
  ${Container}:hover & {
    visibility: visible;
  }
`;

const ReviewsMySummaryRow = ({ employee, deadline, review, showDivider = true, nominationsCount }) => {
  const { displayName, profilePhoto } = employee ?? {};
  const { providerType } = review ?? {};
  const reviewType = PROVIDER_TYPE_TITLE_MAP[providerType];
  const reviewDeadline = deadline ?? Date.now();
  return (
    <>
      <Container flexDir="column">
        <Grid templateColumns="repeat(6, 1fr)" width="100%" gap={2}>
          <GridItem colSpan={4}>
            <TextLabel color="text.g2" mb="4px">
              Review for
            </TextLabel>
            <Flex alignItems="center" sx={{ marginBottom: '8px' }}>
              <Avatar size={24} src={profilePhoto} name={displayName} />
              <TextBodyMedium color="text.g1" ml="8px" className="highlightTextOnHover">
                {displayName}
              </TextBodyMedium>
            </Flex>
            <Flex>
              <TextBodyMedium color="text.g2">
                Review Type: {reviewType}
                {deadline && <>&nbsp;• &nbsp;</>}
              </TextBodyMedium>
              {deadline ? (
                <Flex>
                  <TextBodyMedium color="text.g2">
                    Due:<>&nbsp;</>
                  </TextBodyMedium>
                  <DateComp
                    date={reviewDeadline}
                    isRelativeDateFormat
                    enableRelativeFormatDistanceColor
                    // textComponent={TextBodyMedium}
                    forceRelativeDateFormat
                    showDateInBrackets
                  />
                </Flex>
              ) : null}
            </Flex>
          </GridItem>
          <GridItem colStart={5} colEnd={7}>
            <ButtonContainer className="showOnHover">
              <ReviewActionButton review={review} nominationsCount={nominationsCount} />
            </ButtonContainer>
          </GridItem>
        </Grid>
      </Container>
      <Divider color="neutral.CFD7DF" />
    </>
  );
};

const ReviewsMySummaryCard = ({ employeeId }) => {
  const { data, isLoading, isError } = useMySummaryReviews({
    limit: 3,
    offset: 0,
    sectionId: 'PENDING_ON_ME',
    refetchOnWindowFocus: false,
  });

  const description = checkUserIsReviewAdmin()
    ? 'View a quick summary of your actions in the current active review cycle. Please set up the first review cycle for your company.'
    : 'View a quick summary of your actions in the current active review cycle. Currently, you have no active review cycle set up by your admin.';

  if (isLoading) {
    return (
      <MySummaryCardTemplate showViewAllButton={false} Icon={ReviewsIcon} heading="Reviews">
        <div style={{ marginTop: '16px' }}>
          <SkeletonLoader />
        </div>
      </MySummaryCardTemplate>
    );
  }
  if (isError) {
    return null;
  }

  if (!data?.data?.length) {
    return (
      <EmptySummaryCard
        Icon={ReviewsIcon}
        heading="Reviews"
        description={description}
        illustrationSrc={NoDataImg}
        redirectUrl={
          checkUserIsReviewAdmin()
            ? getModuleSettingsRoute({
                module: 'status',
                activeTabId: 'review_cycles',
              })
            : null
        }
      />
    );
  }

  return (
    <MySummaryCardTemplate
      Icon={ReviewsIcon}
      heading="Reviews"
      viewAllText="View All Reviews"
      viewAllHref={routes.performanceReview.index}
      onClickViewAll={() =>
        sendNavigatedToReviewsEvent({
          eventProperties: {
            source: 'My Summary',
          },
        })
      }
    >
      {data?.data?.map(({ employee, deadline, review, nominationsCount }, index) => (
        <ReviewsMySummaryRow
          employee={employee}
          deadline={deadline}
          review={review}
          key={`${employee?.displayName}-${index}-review-row`}
          showDivider={data?.data?.length - 1 !== index}
          nominationsCount={nominationsCount}
        />
      ))}
    </MySummaryCardTemplate>
  );
};

export { ReviewsMySummaryCard };
